import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Transient notices, mostly for actions that failed.
 *
 * About fifteen mutations across the app had no error handling at all: delete a
 * recording, stop a live meeting, remove a member, revoke an invitation, rename
 * a chat, suspend a workspace. On failure the spinner stopped and nothing else
 * happened — indistinguishable from success, which for "Stop recording" means
 * the user walks away believing a meeting is no longer being recorded.
 *
 * Patching fifteen call sites would have left the sixteenth silent. Instead the
 * QueryClient reports every unhandled mutation error here, so silence is now the
 * exception a developer has to opt into rather than the default they get by
 * forgetting.
 *
 * A module-level emitter rather than context: React Query's `onError` default
 * lives outside the React tree, so it has no hook to call.
 */

export type Toast = {
  id: number;
  tone: "error" | "success";
  message: string;
};

type Listener = (toasts: Toast[]) => void;

let toasts: Toast[] = [];
let nextId = 1;
const listeners = new Set<Listener>();

function emit() {
  for (const listener of listeners) listener(toasts);
}

function dismiss(id: number) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

export function toast(message: string, tone: Toast["tone"] = "error") {
  const id = nextId++;
  // Cap the stack. A failing poll could otherwise queue one per attempt and
  // bury the screen.
  toasts = [...toasts.slice(-2), { id, tone, message }];
  emit();

  // Errors linger longer: they are usually the thing the user needs to read.
  setTimeout(() => dismiss(id), tone === "error" ? 8000 : 4000);
}

export function Toaster() {
  const { t } = useTranslation("shell");
  const [current, setCurrent] = useState<Toast[]>(toasts);

  useEffect(() => {
    listeners.add(setCurrent);
    return () => {
      listeners.delete(setCurrent);
    };
  }, []);

  if (current.length === 0) return null;

  return (
    <div
      // Announced without stealing focus — the action already happened, and
      // moving focus mid-task is worse than the failure.
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-[max(1rem,env(safe-area-inset-bottom))] z-[100] flex flex-col items-center gap-2 px-4"
    >
      {current.map((item) => (
        <div
          key={item.id}
          className={cn(
            "pointer-events-auto flex w-full max-w-[440px] items-start gap-2.5",
            "rounded-xl border px-3.5 py-2.5 shadow-[var(--shadow-md)]",
            item.tone === "error"
              ? "border-danger/40 bg-danger-subtle"
              : "border-border bg-surface",
          )}
        >
          {item.tone === "error" ? (
            <AlertTriangle size={15} className="mt-0.5 shrink-0 text-danger" />
          ) : (
            <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-success" />
          )}
          <p
            className={cn(
              "min-w-0 flex-1 text-[13px] leading-relaxed",
              item.tone === "error" ? "text-danger" : "text-text",
            )}
          >
            {item.message}
          </p>
          <button
            type="button"
            onClick={() => dismiss(item.id)}
            aria-label={t("toast.dismiss")}
            className="shrink-0 rounded-md p-0.5 text-text-faint transition-colors hover:text-text"
          >
            <X size={13} />
          </button>
        </div>
      ))}
    </div>
  );
}
